/**
 * @file Shared form modal shell helpers.
 */

import { clearFormItemData } from "./formModalState.js";
import { destroyRegisteredControlsIn } from "../ui/controls/controlCore.js";
import {
    lockPageScroll,
    unlockPageScroll,
} from "../ui/pageScrollLock.js";

export const formModalElement = document.getElementById("form-modal");

let currentModalCloseCallback = null;
let currentModalCloseGuard = null;
let modalReturnFocusTarget = null;
let modalKeydownHandler = null;
let modalFocusIntentToken = 0;
const FORM_MODAL_SCROLL_LOCK = Symbol("form-modal-scroll-lock");
const FOCUS_NAVIGATION_KEYS = new Set([
    "Tab",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

function invalidateModalFocusIntent() {
    modalFocusIntentToken += 1;
}

function canReceiveModalFocus(target) {
    return Boolean(target?.isConnected &&
        target.getClientRects().length > 0 &&
        !target.closest('[aria-hidden="true"]') &&
        !target.closest("[inert]"));
}

function closingOverlayStillOwnsFocus(closingFocus) {
    const active = document.activeElement;
    return active === document.body || active === closingFocus ||
        Boolean(formModalElement?.contains(active));
}

document.addEventListener("pointerdown", invalidateModalFocusIntent, true);
document.addEventListener("focusin", invalidateModalFocusIntent, true);
document.addEventListener("keydown", (event) => {
    if (FOCUS_NAVIGATION_KEYS.has(event.key)) {invalidateModalFocusIntent();}
}, true);

function getModalTabStops() {
    if (!formModalElement) {return [];}
    return Array.from(formModalElement.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), ' +
        'select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0 &&
        element.getAttribute("aria-hidden") !== "true");
}

/**
 * Activates shared dialog focus ownership after a form modal is rendered.
 * @param {HTMLElement|null} [initialFocus] Preferred initial focus target.
 * @returns {void}
 */
export function activateFormModalAccessibility(initialFocus = null) {
    if (!formModalElement) {return;}
    const focusBeforeActivation = document.activeElement;
    if (formModalElement.getAttribute("aria-hidden") === "true") {
        modalReturnFocusTarget = document.activeElement instanceof HTMLElement ?
            document.activeElement : null;
    }
    formModalElement.setAttribute("aria-hidden", "false");
    if (modalKeydownHandler) {
        document.removeEventListener("keydown", modalKeydownHandler, true);
    }
    modalKeydownHandler = (event) => {
        if (event.key !== "Tab" ||
            document.getElementById("confirm-modal")?.classList.contains("flex")) {return;}
        const stops = getModalTabStops();
        if (!stops.length) {
            event.preventDefault();
            return;
        }
        const first = stops[0];
        const last = stops.at(-1);
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        } else if (!formModalElement.contains(document.activeElement)) {
            event.preventDefault();
            first.focus();
        }
    };
    document.addEventListener("keydown", modalKeydownHandler, true);
    const focusToken = ++modalFocusIntentToken;
    requestAnimationFrame(() => {
        if (focusToken !== modalFocusIntentToken ||
            !formModalElement.classList.contains("flex") ||
            formModalElement.getAttribute("aria-hidden") === "true") {return;}
        const active = document.activeElement;
        if (active !== document.body && active !== focusBeforeActivation &&
            !formModalElement.contains(active)) {return;}
        const target = initialFocus && !initialFocus.disabled ?
            initialFocus : getModalTabStops()[0];
        if (canReceiveModalFocus(target)) {target.focus();}
    });
}

function deactivateFormModalAccessibility() {
    const closingFocus = document.activeElement;
    formModalElement?.setAttribute("aria-hidden", "true");
    if (modalKeydownHandler) {
        document.removeEventListener("keydown", modalKeydownHandler, true);
        modalKeydownHandler = null;
    }
    const returnTarget = modalReturnFocusTarget;
    modalReturnFocusTarget = null;
    if (returnTarget) {
        const focusToken = ++modalFocusIntentToken;
        requestAnimationFrame(() => {
            if (focusToken !== modalFocusIntentToken ||
                formModalElement?.getAttribute("aria-hidden") !== "true" ||
                !closingOverlayStillOwnsFocus(closingFocus) ||
                !canReceiveModalFocus(returnTarget)) {return;}
            returnTarget.focus({preventScroll: true});
        });
    }
}

if (formModalElement && typeof MutationObserver === "function") {
    const modalVisibilityObserver = new MutationObserver(() => {
        if (formModalElement.classList.contains("flex") &&
            formModalElement.getAttribute("aria-hidden") === "true") {
            activateFormModalAccessibility();
        }
    });
    modalVisibilityObserver.observe(formModalElement, {
        attributeFilter: ["class"],
        attributes: true,
    });
}

function createDefaultSubmitButton() {
    const button = document.createElement("button");
    button.type = "submit";
    return button;
}

/**
 * Stores a callback that runs when the form modal closes.
 * @param {Function | null | undefined} callback Close callback.
 * @returns {void}
 */
export function setModalCloseCallback(callback) {
    currentModalCloseCallback =
        typeof callback === "function" ? callback : null;
}

/**
 * Clears the active modal close callback without running it.
 * @returns {void}
 */
export function clearModalCloseCallback() {
    currentModalCloseCallback = null;
}

/**
 * Registers a guard for user-initiated closes of the active form modal.
 * @param {Function | null | undefined} guard Active-modal close guard.
 * @returns {void}
 */
export function setModalCloseGuard(guard) {
    currentModalCloseGuard = typeof guard === "function" ? guard : null;
}

/**
 * Requests a user-initiated close through the active modal guard.
 * @returns {void}
 */
export function requestFormModalClose() {
    if (typeof currentModalCloseGuard === "function" &&
        currentModalCloseGuard() !== false) {
        return;
    }
    closeForm();
}

/**
 * Locks background scroll for the shared form modal.
 * @returns {void}
 */
export function lockFormModalScroll() {
    lockPageScroll(FORM_MODAL_SCROLL_LOCK);
}

/**
 * Releases the shared form modal background scroll lock.
 * @returns {void}
 */
export function unlockFormModalScroll() {
    unlockPageScroll(FORM_MODAL_SCROLL_LOCK);
}

/**
 * Restores the standard Cancel/Save action footer.
 * @returns {void}
 */
export function configureDefaultFormActions() {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const itemForm = formModalElement?.querySelector("#item-form");
    const submitBtn =
        formModalElement?.querySelector("button[type=\"submit\"]") ||
        createDefaultSubmitButton();
    const inviteDraftBtn = document.getElementById("invite-save-draft-btn");
    const actionGroup = document.getElementById("invite-form-action-group");
    const footer = cancelBtn?.parentElement;

    if (!cancelBtn || !itemForm || !footer) {return;}

    currentModalCloseGuard = null;
    cancelBtn.onclick = requestFormModalClose;

    itemForm
        .querySelectorAll(
            [
                "[data-calendar-event-error]",
                "[data-calendar-event-date-error]",
                "[data-calendar-leave-error]",
                "[data-calendar-leave-date-error]",
            ].join(", "),
        )
        .forEach((element) => element.remove());

    footer.querySelectorAll('[data-modal-custom-actions="true"]')
        .forEach((element) => element.remove());
    if (inviteDraftBtn) {
        inviteDraftBtn.remove();
    }
    if (actionGroup && actionGroup !== footer) {
        actionGroup.remove();
    }

    footer.className = "mt-3 flex flex-wrap justify-end gap-3 px-2 shrink-0";
    cancelBtn.className =
        "bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-3 rounded-lg sm:px-4";
    cancelBtn.disabled = false;
    cancelBtn.textContent = "Cancel";

    submitBtn.dataset.submitAction = "save";
    submitBtn.hidden = false;
    submitBtn.className =
        "rounded-lg bg-[var(--dfc-brand-primary)] px-3 py-2 font-bold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)] sm:px-4";
    submitBtn.textContent = "Save";
    submitBtn.disabled = false;
    footer.append(cancelBtn, submitBtn);
}

/**
 * Configures user-invite Save Draft / Send footer actions.
 * @returns {void}
 */
export function configureInviteFormActions() {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = formModalElement?.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement;

    if (!cancelBtn || !submitBtn || !footer) {return;}

    footer.className = "invite-form-footer mt-3 flex flex-nowrap items-center justify-between gap-2 px-2 shrink-0";
    cancelBtn.className =
        "bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-lg";
    cancelBtn.disabled = false;
    cancelBtn.textContent = "Cancel";

    let actionGroup = document.getElementById("invite-form-action-group");
    if (!actionGroup) {
        actionGroup = document.createElement("div");
        actionGroup.id = "invite-form-action-group";
        actionGroup.className = "ml-auto flex flex-nowrap items-center justify-end gap-2";
    } else {
        actionGroup.innerHTML = "";
    }

    const draftBtn = document.createElement("button");
    draftBtn.type = "submit";
    draftBtn.id = "invite-save-draft-btn";
    draftBtn.dataset.submitAction = "draft";
    draftBtn.className =
        "bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-3 rounded-lg";
    draftBtn.textContent = "Save Draft";

    submitBtn.dataset.submitAction = "send";
    submitBtn.className =
        "rounded-lg bg-[var(--dfc-brand-primary)] px-3 py-2 font-bold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)]";
    submitBtn.textContent = "Send Invite";
    submitBtn.disabled = false;

    actionGroup.append(draftBtn, submitBtn);
    footer.append(cancelBtn, actionGroup);
}

/**
 * Closes the main form modal and resets shared modal shell state.
 * @returns {void}
 */
export function closeForm() {
    currentModalCloseGuard = null;
    if (formModalElement) {
        formModalElement.classList.remove("flex");
        deactivateFormModalAccessibility();
        unlockFormModalScroll();
        const formFields = formModalElement.querySelector("#form-fields");
        if (formFields) {
            destroyRegisteredControlsIn(formFields);
            formFields.innerHTML = "";
        }
        formModalElement.querySelector("#form-header-row")?.classList.replace("mb-3", "mb-6");

        const cancelBtn = document.getElementById("cancel-form-btn");
        const submitBtn = formModalElement.querySelector("button[type=\"submit\"]");
        const itemForm = formModalElement.querySelector("#item-form");
        itemForm?.__calendarDateValidationObserver?.disconnect?.();
        if (itemForm) {
            itemForm.__calendarDateValidationObserver = null;
        }
        if (itemForm?.__calendarDateValidationCaptureHandler) {
            document.removeEventListener(
                "input",
                itemForm.__calendarDateValidationCaptureHandler,
                true,
            );
            document.removeEventListener(
                "change",
                itemForm.__calendarDateValidationCaptureHandler,
                true,
            );
            itemForm.__calendarDateValidationCaptureHandler = null;
        }
        if (itemForm?.__calendarDateValidationInterval) {
            clearInterval(itemForm.__calendarDateValidationInterval);
            itemForm.__calendarDateValidationInterval = null;
        }
        const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;
        if (footer) {
            footer.style.display = "";
        }
        if (cancelBtn) {
            cancelBtn.style.display = "";
            cancelBtn.textContent = "Cancel";
        }
        if (submitBtn) {
            submitBtn.style.display = "";
            submitBtn.textContent = "Save";
            submitBtn.disabled = false;
            submitBtn.classList.remove("opacity-50", "cursor-not-allowed");
        }
        configureDefaultFormActions();
    }

    if (typeof currentModalCloseCallback === "function") {
        currentModalCloseCallback();
        currentModalCloseCallback = null;
    }

    clearFormItemData();
}

document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || event.defaultPrevented ||
        !formModalElement?.classList.contains("flex") ||
        document.getElementById("confirm-modal")?.classList.contains("flex")) {
        return;
    }
    event.preventDefault();
    requestFormModalClose();
});
